import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LayoutState } from '@common/services/layout-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Relationship } from '@src/suite/common/data-models/complex-types/relationship';
import { EnpsField } from '@src/suite/common/data-models/fields/enps-field';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { OPTION_BLANK } from '@src/suite/common/enums/constants';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { BasicContentService } from '@src/suite/common/services/basic-content-service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { FullscreenActions, OpenFullscreenModalActions, selectFullscreenNewRecord, selectFullscreenNewRecordDialogState } from '@src/suite/common/store/fullscreen';
import { ListActions } from '@src/suite/common/store/list/list.actions';
import { selectCurrentViewState } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { AssignmentRecordActions, RecordHttpActions, StoriesRecordActions, TopicRecordActions } from '@src/suite/common/store/records/records.actions';
import { selectAssignmentRecordsMap, selectStoryRecordsMap, selectStoryTemplatesMap } from '@src/suite/common/store/records/records.selectors';
import { cleanupRecord, initializeRecord } from '@src/suite/common/utils/record-utils';
import { navigateTo } from '@src/suite/common/utils/store-utils';
import { WireApiActions } from '@src/suite/discover/store/actions/discover.actions';
import { PlaybookSearchService } from '@src/suite/playbook/services/playbook-search-service';
import { PlaybookService } from '@src/suite/playbook/services/playbook-service';
import { catchError, exhaustMap, from, map, mergeMap, of, switchMap } from 'rxjs';
import { v4 } from 'uuid';

import { PlaybookActions, PlaybookApiActions } from './playbook.actions';
import { selectTreeControl } from './playbook.selector';

@Injectable()
export class PlaybookEffects {
    private actions$: Actions = inject(Actions);
    private playbookSearchService: PlaybookSearchService = inject(PlaybookSearchService);
    private playbookService: PlaybookService = inject(PlaybookService);
    private basicContentService: BasicContentService = inject(BasicContentService);
    private nomWebServicesJson: NomWebServicesJson = inject(NomWebServicesJson);
    private dialog: MatDialog = inject(MatDialog);
    private store: Store = inject(Store);
    private viewStateService: LayoutState = inject(LayoutState);

    createAssignment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.createAssignment),
            concatLatestFrom(() => {
                return [this.store.select(selectAssignmentRecordsMap), this.store.select(selectFullscreenNewRecord)];
            }),
            exhaustMap(([action, assignments, newRecord]) => {
                if (newRecord != null) {
                    // TODO: check this
                    const newAssignment = this.playbookService.genenaretNewAssignment(newRecord, action.assignmentType);
                    newAssignment.Guid = v4();
                    newAssignment.isNewRecord = true;
                    newAssignment.Title = newRecord.Slug || newRecord.Title;
                    return of(FullscreenActions.addNewAssignmentToNewRecord({ assignment: newAssignment }));
                } else {
                    const path = `${action.topic.Path.fullPath}\\R_${action.topic.Guid}`;
                    const { Database } = action.topic;
                    return from(this.playbookService.createNewAssignmentV2(Database, path, action.assignmentType, action.topic)).pipe(
                        exhaustMap(res => {
                            const newMap = new Map(assignments);
                            newMap.set(res.data.Guid, res.data);

                            // TODO: fix this in the createNewAssignmentV2 response
                            const updatedTopic = { ...action.topic };
                            if (!action.topic.Sequence?.includes(res.data.Guid)) {
                                updatedTopic.Sequence = [...(action.topic.Sequence || []), res.data.Guid];
                            }
                            return [
                                TopicRecordActions.updateTopics({ records: [updatedTopic] }),
                                AssignmentRecordActions.setAssignments({ records: newMap }),
                                RecordHttpActions.saveRecordChanges({ fieldsToUpdate: [{ key: PSTFieldNames.Type, value: action.topic.Type }], record: updatedTopic }),
                            ];
                        })
                    );
                }
            })
        );
    });

    createStoryFromTemplate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.createStoryFromTemplate),
            concatLatestFrom(() => [this.store.select(selectStoryTemplatesMap), this.store.select(selectStoryRecordsMap)]),
            exhaustMap(([action, storyTemplatesMap, currentStoriesMap]) => {
                if (action.topic.isNewRecord) {
                    const stories = [];
                    for (const templateGuid of action.templateGuids) {
                        const template = storyTemplatesMap.get(templateGuid);
                        const templateStory = initializeRecord<StorytellingStoryRecord>(ObjectTypes.SCRIPT, { ...template });
                        this.playbookService.initializeNewStoryRecordV2(templateStory as StorytellingStoryRecord, action.topic);
                        if (!template || template.Guid === OPTION_BLANK || (!template.Title.trim() && !template.Slug.trim())) {
                            templateStory.Title = action.topic.Title || action.topic.Slug;
                            templateStory.Slug = templateStory.Title;
                        }
                        templateStory.FullStorySlug = templateStory.Title;
                        templateStory.Guid = v4();
                        templateStory.isNewRecord = true;
                        stories.push(templateStory);
                    }
                    return [FullscreenActions.addNewStoriesToNewRecord({ stories })];
                } else {
                    const updatedTopic = { ...action.topic };

                    const templateGuids = action.templateGuids.filter(guid => !!guid);
                    return from(this.playbookService.createNewStoryFromTemplate(updatedTopic.Database, updatedTopic, action.templateProgramId, templateGuids)).pipe(
                        exhaustMap(res => {
                            if (res?.status) {
                                const newMap = new Map(currentStoriesMap);
                                for (const story of res.data) {
                                    newMap.set(story.Guid, story);
                                }
                                const actions = [];
                                actions.push(
                                    ...[
                                        TopicRecordActions.updateTopics({ records: [updatedTopic] }),
                                        StoriesRecordActions.setStories({ records: newMap }),
                                        RecordHttpActions.saveRecordChanges({ fieldsToUpdate: [{ key: PSTFieldNames.Type, value: action.topic.Type }], record: updatedTopic }),
                                    ]
                                );

                                if (action.openStory && action.templateGuids.length === 1) {
                                    actions.push(FullscreenActions.openStory({ recordGuid: res.data[0].Guid, recordType: ObjectTypes.SCRIPT, fromPinnedItem: true }));
                                }
                                actions.push(FullscreenActions.setLoadingProgressBar({ showLoader: false }));
                                return actions;
                            }
                            return [FullscreenActions.noOpAction()];
                        })
                    );
                }
            })
        );
    });

    loadStoryTemplates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.loadStoryTemplates, RecordHttpActions.loadPlaybookObjects),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            mergeMap(([_, vs]) => {
                return from(this.playbookService.loadStoryTemplates(vs)).pipe(
                    map(templates => StoriesRecordActions.setStoryTemplates({ templates })),
                    catchError(error => of(PlaybookApiActions.loadTemplatesError({ error: error })))
                );
            })
        );
    });

    loadTopicTemplates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.loadTopicTemplates, RecordHttpActions.loadPlaybookObjects),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            mergeMap(([_, vs]) => {
                return from(this.playbookService.loadTopicTemplates(vs)).pipe(
                    map(templates => TopicRecordActions.setTopicTemplates({ templates })),
                    catchError(error => of(PlaybookApiActions.loadTemplatesError({ error: error })))
                );
            })
        );
    });

    navigateToTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookActions.navigateToTopic),
            concatLatestFrom(() => this.store.select(selectTreeControl)),
            map(([action, treecontrol]) => {
                const nextItem = navigateTo(action.direction, action.currentNodeUuid, treecontrol);
                return ListActions.navigateObject({ nodeUuid: nextItem.uniqueId });
            })
        );
    });

    prepareCreateTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.prepareCreateTopic),
            concatLatestFrom(() => this.store.select(selectFullscreenNewRecordDialogState)),
            switchMap(([action, state]) => {
                if (state.wireRecord) {
                    return of(WireApiActions.createDiscoverItem({ topic: action.topic, wire: state.wireRecord, createAndNext: action.createAndNext }));
                }
                return of(PlaybookApiActions.createTopic({ topic: action.topic, createAndNext: action.createAndNext }));
            })
        );
    });

    createTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.createTopic),
            concatLatestFrom(() => this.store.select(selectFullscreenNewRecordDialogState)),
            exhaustMap(([action, state]) => {
                let params: { [id: string]: any } = null;
                let topicToCreate = { ...action.topic };
                const topicStories = [...(topicToCreate.stories || [])]?.map(rec => cleanupRecord(rec)) || [];
                const topicAssignments = [...(topicToCreate.children || [])]?.map(rec => cleanupRecord(rec)) || [];
                if (action.discoverItemDatabase && action.discoverItemGuid) {
                    params = {};
                    params.discoverItemDatabase = action.discoverItemDatabase;
                    params.discoverItemPath = action.discoverItemPath.fullPath;
                    params.discoverItemGuid = action.discoverItemGuid;
                    topicToCreate.Relationships = new Relationship();
                    topicToCreate.Relationships.addRelationship(action.discoverItemGuid, ObjectTypes.DISCOVER_ITEM);
                }
                topicToCreate = cleanupRecord(topicToCreate);

                topicToCreate.Title = topicToCreate.Slug;

                return from(this.playbookService.saveNewTopic(topicToCreate, topicAssignments, topicStories, params)).pipe(
                    exhaustMap(res => {
                        const actions = [];
                        actions.push(PlaybookApiActions.createRecordDone());
                        if (res.status) {
                            if (action.createAndNext) {
                                actions.push(FullscreenActions.resetCurrentNewRecord({ recordType: state.record.Type }));
                            } else {
                                actions.push(OpenFullscreenModalActions.closeFullscreenModalForce());
                            }

                            actions.push(TopicRecordActions.updateTopics({ records: [res.data] }));
                            if (res.data?.stories?.length) {
                                actions.push(StoriesRecordActions.updateStories({ records: res.data.stories }));
                            }

                            if (res.data?.children?.length) {
                                actions.push(AssignmentRecordActions.updateAssignments({ records: res.data.children }));
                            }

                            if (state.createInGroupData) {
                                actions.push(ListActions.addListGuids({ listGuids: [res.data.Guid] }));
                            } else {
                                actions.push(ListActions.addNewListGuids({ listGuids: [res.data.Guid] }));
                            }
                        }
                        return actions;
                    })
                );
            }),
            catchError(() => of(PlaybookApiActions.createRecordDone()))
        );
    });

    createStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.createStory),
            concatLatestFrom(() => this.store.select(selectFullscreenNewRecordDialogState)),
            exhaustMap(([action, state]) => {
                let storyToCreate = { ...action.story };
                storyToCreate = cleanupRecord(storyToCreate);
                const storyObject: any = {};
                for (const p in storyToCreate) {
                    if (
                        [
                            'MODTIME',
                            'MODBY',
                            'EXPIRES',
                            'CREATEDDATETIME',
                            'SOURCEGUID',
                            'GUID',
                            'PATH',
                            'PHYSICALPATH',
                            'VIRTUALPATH',
                            'ENPSDATABASE',
                            'CONTAINERTITLE',
                            'PRIORITY',
                            'APPROVED',
                            'OUTPUTTYPE',
                            'CREATOR',
                            'MOSOBJID',
                            'MOSCONTROL',
                            'OWNER',
                            'FILESTORAGEVERSION',
                            'DATABASE',
                        ].indexOf(p.toUpperCase()) >= 0
                    )
                        continue;

                    const value = storyToCreate?.[p];
                    const enpsField = this.nomWebServicesJson.convertor.serializeField(value, p);

                    if (enpsField.key?.toLowerCase() === PSTFieldNames.TextCommands.toLowerCase()) {
                        const textCommandKeys = Object.keys(enpsField.value);
                        const textCommandValues = Object.values(enpsField.value);
                        if (textCommandKeys.length === textCommandValues.length && textCommandKeys.length > 0) {
                            storyObject['TextCommandKeys'] = textCommandKeys;
                            storyObject['TextCommandValues'] = textCommandValues;
                        }
                    } else {
                        storyObject[enpsField.key] = enpsField.value;
                    }
                }

                return from(this.basicContentService.createBasicContent(this.nomWebServicesJson.PSTServer, `P_${this.nomWebServicesJson.PSTGroup}\\W`, false, storyObject)).pipe(
                    exhaustMap(res => {
                        const actions = [];
                        actions.push(PlaybookApiActions.createRecordDone());
                        if (res.status) {
                            if (action.createAndNext) {
                                actions.push(FullscreenActions.resetCurrentNewRecord({ recordType: state.record.Type }));
                            } else {
                                actions.push(OpenFullscreenModalActions.closeFullscreenModalForce());
                            }

                            actions.push(StoriesRecordActions.updateStories({ records: [res.data] }));

                            if (state.createInGroupData) {
                                actions.push(ListActions.addListGuids({ listGuids: [res.data.Guid] }));
                            } else {
                                actions.push(ListActions.addNewListGuids({ listGuids: [res.data.Guid] }));
                            }
                        }
                        return actions;
                    })
                );
            }),
            catchError(() => of(PlaybookApiActions.createRecordDone()))
        );
    });

    saveStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PlaybookApiActions.saveStory),
            exhaustMap(action => {
                const fieldsToUpdate: Array<EnpsField> = [
                    { key: 'Text', value: action.story.text },
                    { key: PSTFieldNames.Type, value: ObjectTypes.SCRIPT },
                ];
                if (action.story.TextCommands) {
                    fieldsToUpdate.push({ key: PSTFieldNames.TextCommands, value: action.story.TextCommands });
                }
                return of(RecordHttpActions.saveRecordChanges({ record: action.story, fieldsToUpdate }));
            })
        );
    });
}
